import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiClock, FiEye, FiHelpCircle,
  FiArrowUpRight, FiCheckCircle, FiAlertCircle,
} from 'react-icons/fi';
import ChainIcon from '../components/ChainIcon';
import CopyButton from '../components/transaction/CopyButton';
import TxDetailsPageHeader from '../components/transaction/TxDetailsPageHeader';
import TxAdditionalInfoPopover from '../components/TxAdditionalInfoPopover';
import SponsoredSection from '../components/SponsoredSection';
import { usePrcWallet } from '../hooks/usePrcWallet';
import { formatRelativeTime } from '../utils/formatTime';

const TYPE_LABELS = {
  swap: 'Swap',
  reward: 'Reward',
  fee: 'Fee',
  transfer: 'Transfer',
};

function PrcWalletDetailsView({ address }) {
  const [page, setPage] = useState(1);
  const { display, loading, error } = usePrcWallet(address, page);
  const [addInfo, setAddInfo] = useState(null);

  const closeAddInfo = useCallback(() => setAddInfo(null), []);

  useEffect(() => {
    if (!addInfo) return undefined;
    const onDocMouseDown = (e) => {
      if (e.target.closest?.('.tx-addinfo-popover') || e.target.closest?.('.tx-eye')) return;
      closeAddInfo();
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') closeAddInfo(); };
    const onScroll = () => closeAddInfo();
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mousedown', onDocMouseDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [addInfo, closeAddInfo]);

  const handleEyeClick = (event, tx) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const popoverTx = {
      signature: tx.hash,
      chain: 'prc',
      action: TYPE_LABELS[tx.type] || 'Transfer',
      success: tx.status !== 'failed',
      amount: tx.amount,
      sender: tx.sender,
      receiver: tx.receiver,
      block: '—',
      time: tx.timestamp ? formatRelativeTime(tx.timestamp) : null,
    };
    setAddInfo((cur) =>
      cur?.signature === tx.hash ? null : { tx: popoverTx, anchor: rect, signature: tx.hash },
    );
  };

  if (loading) {
    return (
      <main className="sections eth-tx-page">
        <TxDetailsPageHeader title="Wallet Details" />
        <div className="stx-skeleton stx-skeleton-panel" />
      </main>
    );
  }

  if (error || !display) {
    return (
      <main className="sections eth-tx-page">
        <TxDetailsPageHeader title="Wallet Details" />
        <div className="stx-error">
          <p>{error || 'Wallet not found.'}</p>
          <Link to="/" className="eth-tx-link">Back to home</Link>
        </div>
      </main>
    );
  }

  const pagination = display.pagination;

  return (
    <main className="sections eth-tx-page">
      <TxDetailsPageHeader title="Wallet Details" />

      <section className="eth-tx-card">
        <div className="eth-tx-row">
          <div className="eth-tx-row__label"><FiHelpCircle aria-hidden /> Address:</div>
          <div className="eth-tx-row__value">
            <ChainIcon chain="prc" size="md" />
            <code className="eth-tx-mono">{display.address}</code>
            <CopyButton text={display.address} className="eth-tx-copy" />
          </div>
        </div>
        <div className="eth-tx-row">
          <div className="eth-tx-row__label">Balance:</div>
          <div className="eth-tx-row__value"><strong>{display.balance}</strong></div>
        </div>
        <div className="eth-tx-row">
          <div className="eth-tx-row__label">Total Sent:</div>
          <div className="eth-tx-row__value">{display.totalSent}</div>
        </div>
        <div className="eth-tx-row">
          <div className="eth-tx-row__label">Total Received:</div>
          <div className="eth-tx-row__value">{display.totalReceived}</div>
        </div>
        <div className="eth-tx-row">
          <div className="eth-tx-row__label">Transactions:</div>
          <div className="eth-tx-row__value">{display.transactionCount?.toLocaleString()}</div>
        </div>
        {display.lastActivityUnix ? (
          <div className="eth-tx-row">
            <div className="eth-tx-row__label">Last Activity:</div>
            <div className="eth-tx-row__value eth-tx-inline">
              <FiClock aria-hidden />
              {formatRelativeTime(display.lastActivityUnix)}
            </div>
          </div>
        ) : null}
        <SponsoredSection compact className="sponsored-section--eth-row" />
      </section>

      {display.transactions?.length > 0 ? (
        <section className="wtx-section">
          <div className="wtx-header">
            <h2 className="wtx-heading">Transaction History</h2>
            <span className="wtx-count">{display.transactionCount?.toLocaleString()} total</span>
          </div>

          <div className="wtx-table-wrap">
            <div className="wtx-table">
              <div className="wtx-row wtx-row--head">
                <span className="wtx-cell wtx-cell--eye" />
                <span className="wtx-cell wtx-cell--hash">Tx Hash</span>
                <span className="wtx-cell wtx-cell--type">Type</span>
                <span className="wtx-cell wtx-cell--amount">Amount</span>
                <span className="wtx-cell wtx-cell--status">Status</span>
                <span className="wtx-cell wtx-cell--time">Time</span>
              </div>

              {display.transactions.map((tx) => {
                const isSuccess = tx.status !== 'failed';
                return (
                  <div key={tx.hash} className="wtx-row">
                    <span className="wtx-cell wtx-cell--eye">
                      <button
                        type="button"
                        className={`tx-eye ${addInfo?.signature === tx.hash ? 'is-active' : ''}`}
                        aria-expanded={addInfo?.signature === tx.hash}
                        aria-haspopup="dialog"
                        aria-label="Transaction details"
                        onClick={(e) => handleEyeClick(e, tx)}
                      >
                        <FiEye />
                      </button>
                    </span>

                    <span className="wtx-cell wtx-cell--hash">
                      <Link to={`/tx/${encodeURIComponent(tx.hash)}`} className="wtx-hash-link">
                        {tx.displayHash}
                      </Link>
                      <CopyButton text={tx.hash} className="tx-copy-icon-btn" label="Copy hash" />
                    </span>

                    <span className="wtx-cell wtx-cell--type">
                      <span className="wtx-type-pill">
                        <FiArrowUpRight />
                        {TYPE_LABELS[tx.type] || tx.type}
                      </span>
                    </span>

                    <span className="wtx-cell wtx-cell--amount">{tx.amount}</span>

                    <span className="wtx-cell wtx-cell--status">
                      {isSuccess ? (
                        <span className="wtx-status wtx-status--ok">
                          <FiCheckCircle />
                          Success
                        </span>
                      ) : (
                        <span className="wtx-status wtx-status--fail">
                          <FiAlertCircle />
                          Failed
                        </span>
                      )}
                    </span>

                    <span className="wtx-cell wtx-cell--time">
                      {tx.timestamp ? formatRelativeTime(tx.timestamp) : '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {pagination && pagination.last_page > 1 ? (
            <div className="wtx-pagination">
              <button
                type="button"
                className="wtx-page-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className="wtx-page-info">Page {page} of {pagination.last_page}</span>
              <button
                type="button"
                className="wtx-page-btn"
                disabled={page >= pagination.last_page}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      {addInfo ? (
        <TxAdditionalInfoPopover
          key={addInfo.signature}
          anchor={addInfo.anchor}
          signature={addInfo.signature}
          tx={addInfo.tx}
          onClose={closeAddInfo}
        />
      ) : null}
    </main>
  );
}

export default PrcWalletDetailsView;
